import { Box } from "@mui/material";
import type { TFunction } from "i18next";
import { DateTime } from "luxon";
import { type FC, Fragment, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { ShiftChangeField, type ShiftFieldChange } from "../../pages/ActivityLog/GroupedActivityLog/useGroupedActivityLogQuery";
import { FONT_SIZE, INK } from "../../theme/tokens";
import { formatLocationLabel } from "../../utils/locationDisplay";
import { formatCurrency } from "../../utils/utilFunctions";
import { ValueChange } from "../UIToolkit/molecules/ValueChange";

type Props = {
	changes: ShiftFieldChange[];
	dataTestId: string;
};

const EMPTY_VALUE = "—";
const TIME_FORMAT = "h:mm a";
const DATED_TIME_FORMAT = "MMM d, h:mm a";
const TIME_FIELDS: ReadonlySet<ShiftChangeField> = new Set([ShiftChangeField.Start, ShiftChangeField.End, ShiftChangeField.CallInTime]);

const FIELD_LABEL_KEY: Record<ShiftChangeField, string> = {
	[ShiftChangeField.Start]: "start.time",
	[ShiftChangeField.End]: "end.time",
	[ShiftChangeField.Location]: "location",
	[ShiftChangeField.Job]: "job",
	[ShiftChangeField.Notes]: "notes",
	[ShiftChangeField.Charge]: "charge",
	[ShiftChangeField.Bonus]: "shiftHistory.field.bonus",
	[ShiftChangeField.CallInTime]: "callin.time",
	[ShiftChangeField.Removed]: "shiftHistory.field.status",
};

type Formatters = { t: TFunction; timezone: string; jobNameById: Map<string, string> };

/** A time reads as a clock time, with its date only when the change moved it to another day. */
const formatTimes = (change: ShiftFieldChange, timezone: string): [string, string] => {
	const before = change.before === null ? null : DateTime.fromISO(change.before).setZone(timezone);
	const after = change.after === null ? null : DateTime.fromISO(change.after).setZone(timezone);
	const format = before !== null && after !== null && !before.hasSame(after, "day") ? DATED_TIME_FORMAT : TIME_FORMAT;
	return [before?.toFormat(format) ?? EMPTY_VALUE, after?.toFormat(format) ?? EMPTY_VALUE];
};

const formatValue = (field: ShiftChangeField, value: string | null, { t, jobNameById }: Formatters): string => {
	if (value === null) return EMPTY_VALUE;
	switch (field) {
		case ShiftChangeField.Location:
			return formatLocationLabel(value);
		case ShiftChangeField.Job:
			// DEFAULT: a job removed from the department since has no name left to show.
			return jobNameById.get(value) ?? EMPTY_VALUE;
		case ShiftChangeField.Charge:
			return t(value === "true" ? "yes" : "no");
		case ShiftChangeField.Bonus:
			return formatCurrency(Number(value));
		case ShiftChangeField.Removed:
			return t(value === "true" ? "shiftHistory.value.removed" : "shiftHistory.value.onSchedule");
		default:
			// The response's date conversion turns ISO-looking text into a DateTime; String() reads it back as text.
			return String(value);
	}
};

/** What one save changed on the shift, each field as it was and as it became. */
export const ShiftFieldChanges: FC<Props> = ({ changes, dataTestId }) => {
	const { t } = useTranslation();
	const { chosenDepartment } = useHospitalData();
	const jobNameById = useMemo(() => new Map((chosenDepartment.jobs ?? []).map((job) => [job.id, job.displayName])), [chosenDepartment.jobs]);
	const formatters: Formatters = { t, timezone: chosenDepartment.timezone, jobNameById };

	return (
		<Box
			component="dl"
			data-testid={dataTestId}
			sx={{ display: "grid", gridTemplateColumns: "auto minmax(0, 1fr)", columnGap: 1.5, rowGap: 0.5, alignItems: "baseline", m: 0, mt: 0.75 }}
		>
			{changes.map((change) => {
				const [before, after] = TIME_FIELDS.has(change.field)
					? formatTimes(change, chosenDepartment.timezone)
					: [formatValue(change.field, change.before, formatters), formatValue(change.field, change.after, formatters)];
				return (
					<Fragment key={change.field}>
						<Box component="dt" sx={{ fontSize: FONT_SIZE.CAPTION, color: INK.MUTED }}>
							{t(FIELD_LABEL_KEY[change.field])}
						</Box>
						<Box component="dd" sx={{ m: 0, minWidth: 0 }}>
							<ValueChange
								before={before}
								after={after}
								beforeLabel={t("shiftHistory.change.before")}
								afterLabel={t("shiftHistory.change.after")}
								dataTestId={`${dataTestId}-${change.field}`}
							/>
						</Box>
					</Fragment>
				);
			})}
		</Box>
	);
};
